import * as React from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { Elements } from '@stripe/react-stripe-js';
import { getConfig } from 'gossamer-front-end';
import { TicketItemWithBookings } from 'cb-model';
import { getBookingPaymentTermsText } from 'cb-unified';
import { useEventPaymentData } from './useEventPaymentData';
import { useStripePaymentIntent } from './useStripePaymentIntent';
import { useStripeLoader } from './useStripeLoader';
import { StripePayForm } from './StripePayForm';

interface EventPaymentCheckoutModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: number;
  ticketId: number;
}

export const EventPaymentCheckoutModal = ({
  open,
  onOpenChange,
  eventId,
  ticketId,
}: EventPaymentCheckoutModalProps): React.JSX.Element => {
  const { eventRecord, bookingPaymentTermsRecord } = useEventPaymentData(eventId);
  const [termsAccepted, setTermsAccepted] = React.useState(false);
  const publishableKey = String(getConfig('stripe-publishable-key') || '');
  const event = eventRecord.data;
  const selectedTicket =
    ((event?.ticket_data || []) as TicketItemWithBookings[]).find((ticket) => ticket.id === ticketId) || null;
  const isPaidTicket = Number(selectedTicket?.price || 0) > 0;
  const bookingPaymentTermsId = event?.booking_payment_terms_id || null;

  const { clientSecret, intentError, isCreatingIntent } = useStripePaymentIntent({
    open,
    ticketId: isPaidTicket ? ticketId : null,
    bookingPaymentTermsId: isPaidTicket ? bookingPaymentTermsId : null,
  });
  const shouldLoadStripe = open && !!publishableKey && !!clientSecret && termsAccepted;
  const {
    stripe,
    error: stripeLoadError,
    isLoading: isLoadingStripe,
    retry: retryStripe,
  } = useStripeLoader(shouldLoadStripe, publishableKey);

  React.useEffect(() => {
    if (open) {
      setTermsAccepted(false);
    }
  }, [open]);

  return (
    <Modal show={open} onHide={() => onOpenChange(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Complete payment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h5>{event?.title || 'Event'}</h5>
        {!!selectedTicket && (
          <p className="mb-2">
            <b>{selectedTicket.title}</b> - £{Number(selectedTicket.price || 0).toFixed(2)}
          </p>
        )}
        {!publishableKey && <Alert variant="danger">Stripe is not configured.</Alert>}
        {!bookingPaymentTermsRecord.data && (
          <Alert variant="danger">This paid event has no booking payment terms configured.</Alert>
        )}
        {!!bookingPaymentTermsRecord.data && (
          <Form.Check
            id="accept-booking-payment-terms"
            className="mb-3"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            label={`I agree to: ${bookingPaymentTermsRecord.data.title}`}
          />
        )}
        {!!bookingPaymentTermsRecord.data?.text && (
          <div className="small text-muted mb-3" style={{ whiteSpace: 'pre-wrap' }}>
            {getBookingPaymentTermsText(bookingPaymentTermsRecord.data)}
          </div>
        )}

        {!!intentError && <Alert variant="danger">{intentError}</Alert>}
        {isCreatingIntent && <Alert variant="secondary">Preparing secure payment form...</Alert>}
        {isLoadingStripe && <Alert variant="secondary">Loading secure payment form...</Alert>}
        {!!stripeLoadError && (
          <Alert variant="danger">
            <div>{stripeLoadError}</div>
            <Button className="mt-2" size="sm" variant="outline-danger" onClick={retryStripe}>
              Try again
            </Button>
          </Alert>
        )}

        {!!stripe && !!clientSecret && termsAccepted && (
          <Elements stripe={stripe} options={{ clientSecret }}>
            <StripePayForm returnUrl={window.location.href} />
          </Elements>
        )}
      </Modal.Body>
    </Modal>
  );
};
